import type { Aabb, PropertyPublic, StoreThemeId, WorldManifest } from "@townsy/contracts";
import { getInterior, getPlot, interiorColliders, interiorDecorationColliders, storeInteriorColliders, type PlacementRef } from "@townsy/world";
import { townBounds, townStreetColliders } from "./townStreet";

/**
 * Collision environment for client-side prediction. It MUST match what the realtime server
 * simulates (ADR-0004 consequences, ADR-0005 "Collision", ADR-0006 "Solid world"): on the street
 * that is every static collider, the street decor of the current ownership state (hedges,
 * mailboxes, For Sale signs, billboard legs...; a plot with a front fence has no hedges), one box
 * per OWNED residential plot (unowned lots are open land) and every solid yard item placed on
 * those plots; inside a store, the wall ring plus the fixtures of its interior theme; inside other
 * rooms, the template's walls and furniture plus, in a home, the furniture its owner placed. Both
 * sides derive the boxes from the same property DTO through `@townsy/world`. A mismatch only shows
 * up as small corrections, never as authority. Cars are separate, moving obstacles (prediction.ts).
 * Town v6 (ADR-0042): the street room is the WHOLE town's; its colliders, bounds and seats come from the whole town's
 * street geometry (`townStreetManifest`, ./townStreet.ts), exactly as the one realtime street room builds them.
 */
export function ownedResidentialIds(properties: Iterable<Pick<PropertyPublic, "id" | "type" | "status">>): Set<string> {
  const owned = new Set<string>();
  for (const p of properties) if (p.type === "RESIDENTIAL" && p.status === "OWNED") owned.add(p.id);
  return owned;
}

/** Stable identity of an owned set, so callers only rebuild colliders when ownership changed. */
export function ownedSetKey(owned: ReadonlySet<string>): string {
  return Array.from(owned).sort().join(",");
}

export type YardPlacement = PlacementRef & { propertyId: string };

/** Yard items placed on owned homes (`PropertyPublic.home.placedItems` with scope "yard"). */
export function yardPlacements(properties: Iterable<Pick<PropertyPublic, "id" | "type" | "status" | "home">>): YardPlacement[] {
  const out: YardPlacement[] = [];
  for (const p of properties) {
    if (p.type !== "RESIDENTIAL" || p.status !== "OWNED" || !p.home) continue;
    for (const item of p.home.placedItems) {
      if ((item.scope ?? "yard") !== "yard") continue;
      out.push({ propertyId: p.id, anchorId: item.anchorId, itemId: item.itemId, rotation: item.rotation });
    }
  }
  return out;
}

/** Furniture placed inside one home (scope "interior"). */
export function interiorPlacements(property: Pick<PropertyPublic, "home"> | null | undefined): PlacementRef[] {
  if (!property?.home) return [];
  return property.home.placedItems
    .filter((i) => i.scope === "interior")
    .map((i) => ({ anchorId: i.anchorId, itemId: i.itemId, rotation: i.rotation }));
}

export interface PredictionRoom {
  kind: "street" | "interior";
  /** The room's district as the realtime client knows it (unused for prediction since town v6: one street). */
  districtId: string | null;
  propertyId: string | null;
}

export interface PredictionEnvironment {
  colliders: Aabb[];
  bounds: Aabb;
}

export interface DecorationPlacements {
  /** Street: yard items of every owned home. */
  yard?: readonly YardPlacement[];
  /** Interior of a home: that home's furniture. */
  interior?: readonly PlacementRef[];
  /** Interior of a store: its theme's fixtures (null/unknown falls back to "general", like the server). */
  storeTheme?: StoreThemeId | null;
}

export function predictionEnvironment(manifest: WorldManifest, room: PredictionRoom, owned: ReadonlySet<string>, decor: DecorationPlacements = {}): PredictionEnvironment {
  if (room.kind === "interior" && room.propertyId) {
    const plot = getPlot(manifest, room.propertyId);
    const template = getInterior(manifest, plot?.interiorTemplate ?? "store_room");
    // Stores: the theme's fixtures REPLACE the template's built-in counter and shelves.
    if (plot?.type === "COMMERCIAL") return { colliders: storeInteriorColliders(template, decor.storeTheme ?? null), bounds: template.bounds };
    const furniture = plot?.type === "RESIDENTIAL" ? interiorDecorationColliders(template, decor.interior ?? []) : [];
    return { colliders: [...interiorColliders(template), ...furniture], bounds: template.bounds };
  }
  // The whole town's street (every district's boundaries, buildings, props, decor and the owned homes / yards).
  return { colliders: townStreetColliders(manifest, owned, decor.yard ?? []), bounds: townBounds(manifest) };
}

/** Everything prediction needs from the live property list for one room. */
export function environmentInputs(
  room: PredictionRoom,
  properties: readonly PropertyPublic[],
): { owned: Set<string>; decor: DecorationPlacements; key: string } {
  if (room.kind === "interior" && room.propertyId) {
    const property = properties.find((p) => p.id === room.propertyId) ?? null;
    const interior = interiorPlacements(property);
    const storeTheme = property?.type === "COMMERCIAL" ? (property.interiorTheme ?? null) : null;
    const key = `interior|${room.propertyId}|${storeTheme ?? ""}|${interior.map((p) => `${p.anchorId}:${p.itemId}:${p.rotation}`).join(",")}`;
    return { owned: new Set(), decor: { interior, storeTheme }, key };
  }
  const owned = ownedResidentialIds(properties);
  const yard = yardPlacements(properties);
  const yardKey = yard
    .map((p) => `${p.propertyId}/${p.anchorId}:${p.itemId}:${p.rotation}`)
    .sort()
    .join(",");
  return { owned, decor: { yard }, key: `street|${ownedSetKey(owned)}|${yardKey}` };
}
